import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { AddPositionForm } from '../forms/AddPositionForm'
import { BranchDetails } from '../BranchDetails'
import {
  AlertDialogSlide,
  CustomizedTreeView,
  customizedTreeViewIconStyles,
  LightTooltip,
  MoreButton,
  SalesTeamIcon,
  SideDrawer,
  SimpleAccordion,
  SimpleAccordionDetails,
  SimpleAccordionSummary,
} from '@sales-center/components'

import {
  ISalesTeam,
  ISalesTeamPosition,
  SalesTeamPositionEdit,
  SalesTeamUser,
} from '@user/store/salesTeam/typings'
import { DeleteSalesTeamDialog } from '../DeleteDialog/DeleteSalesTeamDialog'
import { AdminSalesTeamPosition } from './AdminSalesTeamPosition'
import { Formik, FormikHelpers } from 'formik'
import {
  IFormikUserModel,
  userFormInitialValues,
  validateMappedUserForm,
  validateUnmappedUserForm,
} from '../../Users/UpdateUser/typings'
import { ITenantUserUpdate } from '@user/store/users/typings'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { UpdateUserSideBarNew } from '../../Users/UpdateUser/UpdateUserSideBarNew'
import { getDeleteButtonStyles, getStyles } from './styling'
import { useDeleteSalesTeamPosition } from '@user/store/salesTeam/hooks/useDeleteSalesTeamPosition'
import { useGetBranchDetail } from '@user/store/branches/hooks/useGetBranchDetail'
import { useGetUser } from '@user/store/users/hooks/useGetUser'
import { usePositionsBySalesTeamId } from '@user/store/salesTeam/hooks/usePositionsBySalesTeamId'
import { useUpdateUser } from '../../Users/hooks/useUpdateUser'

import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button, { buttonClasses } from '@mui/material/Button'
import CreateIcon from '@mui/icons-material/Create'
import Card from '@mui/material/Card'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { selectBranchById } from '@user/store/branches/slice'
import { StoreData } from '@user/store/reducers/typings'
import { Branch } from '@user/store/branches/typings'
import { Theme, useTheme } from '@mui/material/styles'
import { useGetLicenseInfo } from '../../Users/hooks/useGetLicenseInfo'
import { adminRoleName, guestRoleName } from '../../Users/UpdateUser/constants'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateLoadingState } from '@sales-center/common'

type Props = {
  handleRefreshList: () => void
  salesTeam: ISalesTeam
  onEdit: (id: number) => void
  onDelete: (id: string) => void
}

export function SalesTeamCard(props: Props) {
  const { handleRefreshList, salesTeam, onEdit, onDelete } = props
  const { id, name, branchId } = salesTeam
  const dispatch = useAppDispatch()

  const branchSummary = useSelector<StoreData, Branch | undefined>((store) =>
    selectBranchById(store, branchId),
  )
  const [licenseLeft, setLicenseLeft] = useState<number | undefined>(undefined)

  const branchName = branchSummary?.name ?? ''

  const allSalesTeams = useSelector(selectAllSalesTeams)

  const deleteButtonStyles = getDeleteButtonStyles(useTheme())

  const positions = usePositionsBySalesTeamId(id)

  const deleteDisabled =
    positions.some((position) => position.positionUsers.length > 0) ||
    salesTeam.inUse ||
    salesTeam.hasUsers

  const deleteTooltip = deleteDisabled
    ? 'Remove all employees from positions and reassign Leads and Opportunities from this sales team to delete it'
    : ''

  //#region branch
  const { branch, setBranchId } = useGetBranchDetail()
  const [expanded, setExpanded] = useState(false)
  const onAccordionExpand = useCallback(
    (_: React.ChangeEvent, isExpanded: boolean) => {
      setBranchId(branchId)
      setExpanded(isExpanded)
    },
    [branchId, setBranchId],
  )
  //#endregion

  //#region add position form
  // ========== S T A T E ==========
  const [editPosFormOpen, setEditPosFormOpen] = useState(false)
  const [editPosition, setEditPosition] = useState<SalesTeamPositionEdit>()
  const [deletePositionId, setDeletePositionId] = useState<number | null>(null)

  useGetLicenseInfo(licenseLeft, setLicenseLeft)

  const showAddPosForm = useCallback(() => setEditPosFormOpen(true), [])

  const handleClosePositionForm = useCallback(() => {
    setEditPosition(undefined)
    setEditPosFormOpen(false)
  }, [])
  //#endregion

  //#region sales team delete
  const [salesTeamDeleteDialogOpen, setSalesTeamDeleteDialogOpen] =
    useState(false)
  const openSalesTeamDeleteDialog = useCallback(
    () => setSalesTeamDeleteDialogOpen(true),
    [],
  )
  const closeSalesTeamDeleteDialog = useCallback(() => {
    setSalesTeamDeleteDialogOpen(false)
    handleRefreshList()
  }, [handleRefreshList])

  const isOnlySalesTeamInBranch = useMemo(() => {
    return Boolean(
      allSalesTeams.filter((team) => team.branchId == salesTeam.branchId)
        .length == 1,
    )
  }, [allSalesTeams, salesTeam.branchId])

  const isOnlySalesTeam = useMemo(() => {
    return Boolean(allSalesTeams.length == 1)
  }, [allSalesTeams])

  const isBranchOrCompanyDefault = useMemo(() => {
    return salesTeam.branchDefault || salesTeam.tenantDefault
  }, [salesTeam.branchDefault, salesTeam.tenantDefault])

  const onDeleteClick = useCallback(() => {
    if (
      isOnlySalesTeamInBranch ||
      isOnlySalesTeam ||
      !isBranchOrCompanyDefault
    ) {
      onDelete(id.toString())
    } else {
      openSalesTeamDeleteDialog()
    }
  }, [
    id,
    isBranchOrCompanyDefault,
    isOnlySalesTeam,
    isOnlySalesTeamInBranch,
    onDelete,
    openSalesTeamDeleteDialog,
  ])

  //#region edit employee form
  const { execute: executeUpdateEmployee, status: updateEmployeeStatus } =
    useUpdateUser(handleRefreshList)

  const {
    setUserId: setEditUserId,
    user: editUser,
    status: getUserStatus,
  } = useGetUser(true, true)

  useEffect(() => {
    if (getUserStatus == 'pending') {
      dispatch(updateLoadingState('visible'))
    }
    if (
      getUserStatus == 'success' ||
      getUserStatus == 'idle' ||
      getUserStatus == 'fail'
    ) {
      dispatch(updateLoadingState('hidden'))
    }
  }, [getUserStatus])

  useEffect(() => {
    if (updateEmployeeStatus == 'pending') {
      dispatch(updateLoadingState('visible'))
    }

    if (updateEmployeeStatus == 'fail') {
      dispatch(updateLoadingState('hidden'))
    }

    if (updateEmployeeStatus == 'success') {
      handleRefreshList()
      dispatch(updateLoadingState('hidden'))
    }
  }, [handleRefreshList, updateEmployeeStatus])

  const editEmployeeDrawerOpen = !!editUser
  const employeeVariant = editUser
    ? editUser.externalUserId
      ? 'mapped'
      : 'unmapped'
    : undefined

  const closeEditEmployeeDrawer = useCallback(() => {
    setEditUserId(undefined)
  }, [setEditUserId])

  const handleEmployeeEditClick = useCallback(
    (event, id?: string) => {
      event.preventDefault()
      if (!id) {
        return
      }
      setEditUserId(id)
    },
    [setEditUserId],
  )

  const handleSubmitEditEmployeeForm = useCallback(
    async (model: IFormikUserModel, formikHelpers: FormikHelpers<any>) => {
      const user: ITenantUserUpdate = {
        emailAddress: model.emailAddress,
        externalUserId: model.externalUser?.id ?? '',
        firstName: model.firstName,
        id: model.id,
        lastName: model.lastName,
        role: model.role,
        roleId: model.role?.id ?? -1,
        isActive: model.isActive,
        salesTeamPositionIds:
          model.role?.name === adminRoleName ||
          model.role?.name === guestRoleName
            ? []
            : model.salesTeamAssignments.map((a) =>
                parseInt(a.salesTeamPositionId),
              ),
        disabledAssignmentRulesCount: null,
      }

      if (user.id && user.id.length > 0) {
        if (editUser && editUser.emailAddress !== user.emailAddress)
          user.emailUpdated = true
        executeUpdateEmployee(user)
      }
      closeEditEmployeeDrawer()
      formikHelpers.resetForm()
    },
    [closeEditEmployeeDrawer, executeUpdateEmployee, adminRoleName],
  )

  //#endregion

  const onEditClick = useCallback(() => onEdit(id), [id, onEdit])

  const deleteSalesTeamPosition = useDeleteSalesTeamPosition()

  const [showTreePositionDeleteConfirm, setShowTreePositionDeleteConfirm] =
    useState<boolean>(false)

  const handleTreeAddPositionClick = useCallback(
    (event, id?: number) => {
      event.preventDefault()
      if (!id) {
        return
      }
      const positionToAddTo = positions.find((p) => p.id == id)
      if (positionToAddTo) {
        setEditPosition({
          parentId: id,
          salesTeamId: props.salesTeam.id,
          title: '',
        })
        showAddPosForm()
      }
    },
    [positions, props.salesTeam.id, showAddPosForm],
  )

  const handleTreeDeletePositionClick = useCallback((event, id?: number) => {
    event.preventDefault()
    if (!id) {
      return
    }
    setDeletePositionId(id)
    setShowTreePositionDeleteConfirm(true)
  }, [])

  const handleDeletePositionConfirm = useCallback(() => {
    if (deletePositionId) {
      deleteSalesTeamPosition({
        id: props.salesTeam.id,
        positionId: deletePositionId,
      })
      setShowTreePositionDeleteConfirm(false)
    }
  }, [deletePositionId, deleteSalesTeamPosition, props.salesTeam.id])

  const handleTreeEditPositionClick = useCallback(
    (event, id?: number) => {
      event.preventDefault()
      if (!id) {
        return
      }
      const positionToEdit = positions.find((p) => p.id == id)
      if (positionToEdit) {
        setEditPosition(positionToEdit)
        showAddPosForm()
      }
    },
    [positions, showAddPosForm],
  )

  const DeleteButton = (position: ISalesTeamPosition) => {
    return (
      <Button
        sx={{
          [`&.${buttonClasses.text}`]:
            position.positionAssignmentsCount > 0
              ? {}
              : { ...deleteButtonStyles.text },
        }}
        disabled={position.positionAssignmentsCount > 0}
        onClick={(event) => handleTreeDeletePositionClick(event, position.id)}
        variant="text"
      >
        delete
      </Button>
    )
  }

  const treeViewButtons = (position: ISalesTeamPosition) => {
    return (
      <>
        <IconButton
          onClick={(event) => handleTreeAddPositionClick(event, position.id)}
          size="large"
          data-test-id={`salesTeamAddPositionBtn-${position.id}`}
        >
          <AddIcon
            sx={{
              ...customizedTreeViewIconStyles.root,
              margin: (theme: Theme) => theme.spacing(-1.2),
              width: 20,
              height: 20,
            }}
          />
        </IconButton>
        <IconButton
          onClick={(event) => handleTreeEditPositionClick(event, position.id)}
          size="large"
        >
          <CreateIcon
            sx={{
              ...customizedTreeViewIconStyles.root,
              margin: (theme: Theme) => theme.spacing(-1.2),
              width: 14,
              height: 14,
            }}
          />
        </IconButton>
        {position.positionAssignmentsCount > 0 ? (
          <LightTooltip
            title={`You must remove ${
              position.positionAssignmentsCount
            } employee${
              position.positionAssignmentsCount === 1 ? '' : 's'
            } from this position to delete it`}
          >
            <span>
              <DeleteButton {...position} />
            </span>
          </LightTooltip>
        ) : (
          <DeleteButton {...position} />
        )}
      </>
    )
  }

  const employeeButtons = (employee: SalesTeamUser) => {
    if (employee.isExternalUser && employee.isActive == false) {
      return null
    }

    return (
      <>
        <IconButton
          onClick={(event) => handleEmployeeEditClick(event, employee.userId)}
          size="large"
        >
          <CreateIcon
            sx={{
              ...customizedTreeViewIconStyles.root,
              margin: (theme: Theme) => theme.spacing(-1.2),
              width: 14,
              height: 14,
            }}
          />
        </IconButton>
      </>
    )
  }

  const positionSlideOutHeading =
    editPosition == undefined || editPosition.id == undefined
      ? 'Add Position'
      : 'Edit Position'
  const styles = getStyles(useTheme())

  const mappedPositions = positions.map((row) => {
    return {
      ...row,
      positionUsers: row.positionUsers.map((user) => {
        return {
          ...user,
          userName:
            user.isActive == false
              ? `${user.userName} (Inactive)`
              : user.userName,
        }
      }),
    }
  })
  return (
    <>
      <Card
        sx={{ ...styles.card }}
        elevation={2}
        square
        data-test-id="salesTeamCard"
      >
        <Box sx={{ ...styles.topSection }}>
          <SalesTeamIcon style={{ ...styles.topContent }} />
          <Box sx={{ ...styles.teamDataSection }}>
            <Box
              component="span"
              sx={{ ...styles.teamData, ...styles.label }}
              style={{ fontSize: '16px' }}
            >
              {name}
            </Box>
          </Box>

          <Box sx={{ ...styles.topContent, ...styles.actionSection }}>
            <div>
              <Button
                color="primary"
                disableElevation
                variant="contained"
                onClick={showAddPosForm}
              >
                Add Position
              </Button>
              <MoreButton
                dataTestId={`salesTeamMoreBtn-${salesTeam.id}`}
                options={[
                  {
                    label: 'Edit',
                    onClick: onEditClick,
                    dataTestId: 'salesTeamEditOption',
                  },
                  {
                    label: 'Delete',
                    onClick: onDeleteClick,
                    disabled: deleteDisabled,
                    tooltipText: deleteTooltip,
                    dataTestId: 'salesTeamDeleteOption',
                  },
                ]}
              />
            </div>
          </Box>
        </Box>

        <SideDrawer
          title={positionSlideOutHeading}
          open={editPosFormOpen}
          onClose={handleClosePositionForm}
        >
          <AddPositionForm
            positionId={editPosition?.id ?? undefined}
            initialParentId={editPosition?.parentId ?? null}
            salesTeam={salesTeam}
            onClose={handleClosePositionForm}
            title={editPosition?.title}
          />
        </SideDrawer>

        <Box my={2}>
          {salesTeam.branchDefault == true && (
            <Typography>Default Sales Team for {branchName}</Typography>
          )}
          {salesTeam.tenantDefault == true && (
            <Typography>Company Default Sales Team</Typography>
          )}
        </Box>

        <Box sx={styles.label}>BRANCH</Box>
        <SimpleAccordion
          expanded={expanded}
          onChange={onAccordionExpand}
          square
          sx={{
            paddingLeft: (theme) => theme.spacing(0.8),
          }}
        >
          <SimpleAccordionSummary
            expandIcon={<ExpandMoreIcon fontSize="small" />}
          >
            <div>{branchName}</div>
          </SimpleAccordionSummary>
          <SimpleAccordionDetails>
            <BranchDetails branch={branch} sx={{ ...styles.branchDetail }} />
          </SimpleAccordionDetails>
        </SimpleAccordion>

        <Box mt={(theme: Theme) => theme.spacing(2.4)} sx={{ ...styles.label }}>
          SALES TEAM STRUCTURE
        </Box>
        <AdminSalesTeamPosition />
        <Divider sx={{ ...styles.divider }} />
        {positions.length > 0 ? (
          <CustomizedTreeView
            buttons={treeViewButtons}
            expandAll={true}
            items={mappedPositions}
            idPropName="id"
            labelPropName="title"
            parentIdPropName="parentId"
            subItems={{
              buttons: employeeButtons,
              collectionName: 'positionUsers',
              itemId: 'userId',
              itemLabel: 'userName',
            }}
            customCheckHasChildrenFn={(row) => row?.positionUsers?.length > 0}
          />
        ) : (
          <Typography component="em" sx={{ ...styles.noPositionsText }}>
            No Sales Team Positions
          </Typography>
        )}
      </Card>
      {/* ========== D E L E T E   C O N F I R M A T I O N   D I A L O G ========== */}
      <AlertDialogSlide
        message="Are you sure you want to delete this position?"
        title="Delete Position"
        okButtonText="Delete"
        open={showTreePositionDeleteConfirm}
        cancelClick={() => setShowTreePositionDeleteConfirm(false)}
        okClick={handleDeletePositionConfirm}
      />
      <DeleteSalesTeamDialog
        salesTeam={salesTeam}
        onClose={closeSalesTeamDeleteDialog}
        onDelete={onDelete}
        open={salesTeamDeleteDialogOpen}
      />
      {/* ========== E D I T   E M P L O Y E E   D R A W E R ========== */}
      <Formik
        enableReinitialize
        initialValues={userFormInitialValues}
        onSubmit={handleSubmitEditEmployeeForm}
        validate={
          employeeVariant == 'mapped'
            ? validateMappedUserForm
            : validateUnmappedUserForm
        }
      >
        {editEmployeeDrawerOpen ? (
          <UpdateUserSideBarNew
            onClose={closeEditEmployeeDrawer}
            open={editEmployeeDrawerOpen}
            user={editUser}
            variant={employeeVariant}
            licenseLeft={licenseLeft}
            initialActive={editUser?.isActive}
          />
        ) : null}
      </Formik>
    </>
  )
}
